import React, { useState } from 'react';
import { Box, Container, Typography, Button, Stack, Grid } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { brandColors } from '../theme';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { ValueProposition } from '../components/marketing/ValueProposition';
import { SEO } from '../components/SEO';
import { organizationSchema, websiteSchema } from '../components/SEO/schemas';
import { FAQSection, FAQTemplates } from '../components/FAQ/FAQSection';
import { SampleReportsModal } from '../components/marketing/SampleReportsModal';


const HomePage: React.FC = () => {
  const { navigateTo } = useAppNavigation();
  const [sampleReportsOpen, setSampleReportsOpen] = useState(false);

  // Combine organization and website schemas for homepage
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [organizationSchema, websiteSchema]
  };

  return (
    <>
      <SEO
        title="Premium Hospitality Market Intelligence & Reports"
        description="Access comprehensive market analysis for 150+ North American hospitality markets. Get monthly reports, performance metrics, ADR, RevPAR, occupancy rates, and strategic insights from industry experts."
        canonical="/"
        structuredData={combinedSchema}
      />
      <Box sx={{ backgroundColor: brandColors.white }}>
      {/* HERO SECTION */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 6 }}>
        <Grid container spacing={4} alignItems="flex-start">
          {/* Left: Heading & Intro */}
          <Grid item xs={12} md={6}>
            <Typography
              component="h1"
              sx={{
                color: brandColors.blue,
                fontWeight: 400,
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
                lineHeight: 1.05,
                letterSpacing: '-0.5px',
                mb: 2,
              }}
            >
              MarketCompass
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: brandColors.indigo,
                fontWeight: 500,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                mb: 4,
              }}
            >
              Your Gateway to Premium Hospitality Market Intelligence
            </Typography>
            <Typography variant="body1" sx={{ color: brandColors.mediumGray, lineHeight: 1.8 }}>
              Unlock comprehensive market analysis and strategic insights for destinations worldwide. Join industry leaders who trust Horwath HTL for data-driven decisions.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              sx={{ justifyContent: 'flex-start', alignItems: { xs: 'stretch', sm: 'center' }, mt: 3 }}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigateTo('/global-market-reports')}
                sx={{
                  backgroundColor: brandColors.gold,
                  color: brandColors.white,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(245, 168, 0, 0.3)',
                  '&:hover': {
                    backgroundColor: '#E09600',
                    transform: 'scale(1.02)',
                    boxShadow: '0 6px 16px rgba(245, 168, 0, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                  minWidth: { xs: 200, sm: 'auto' },
                }}
              >
                Explore Markets
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => setSampleReportsOpen(true)}
                sx={{
                  borderColor: brandColors.blue,
                  color: brandColors.blue,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: brandColors.blue,
                    backgroundColor: 'rgba(32, 76, 229, 0.05)',
                    borderWidth: 2,
                    transform: 'scale(1.02)',
                  },
                  transition: 'all 0.3s ease',
                  minWidth: { xs: 200, sm: 'auto' },
                }}
              >
                View Sample Reports
              </Button>
            </Stack>
          </Grid>
          {/* Right: Angled image placeholder (hide below md to avoid overlap when nav collapses) */}
          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                height: { xs: 416, md: 728 },
                backgroundImage: 'url(/images/home-page-image.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'right top',
                backgroundRepeat: 'no-repeat',
                // Create a triangle with a pointed apex at the top, similar to the reference
                clipPath: 'polygon(60% 0, 100% 85%, 0 100%)',
                mt: -25,
                // Make the triangle flow off the right edge of the page
                width: { xs: '120%', md: '140%' },
                ml: 'auto',
                mr: { xs: '-10vw', md: '-12vw' },
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Value Proposition Boxes */}
      <ValueProposition />

      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Grid container spacing={6}>
          {/* Main column */}
          <Grid item xs={12}>
            {/* About the MarketCompass Reports */}
            <Typography variant="h6" sx={{ color: brandColors.indigo, fontWeight: 700, mb: 2 }}>
              About the MarketCompass Reports
            </Typography>
            <Typography variant="body2" sx={{ color: brandColors.mediumGray, lineHeight: 1.9, mb: 2 }}>
              The Horwath HTL MarketCompass Lodging Reports are produced monthly and provide in-depth analysis for investors and economists on the lodging market. The reports begin with a detailed overview of the market area, focusing on economic characteristics, followed by an analysis of each of the six hotel classes: Luxury, Upper Upscale, Upscale, Upper Midscale, Midscale, and Economy.
            </Typography>
            <Typography variant="body2" sx={{ color: brandColors.mediumGray, lineHeight: 1.9, mb: 2 }}>
              The “Market Anatomy” section covers key economic drivers such as air passenger statistics, bond ratings, and other factors influencing the local economy. The reports also present key metrics for each hotel class, including room revenue, pricing, supply, and demand, with historical data dating back to 1990. Additionally, the report includes an analysis of hotel company brand representation by room count and meeting space, investment rates, risk, and air passenger throughput. Forward-looking published rates and a comprehensive interpretation of factors affecting future lodging performance are provided. The report concludes with detailed tables of data, including occupancy, guest-paid ADR, and RevPAR metrics.
            </Typography>

          </Grid>
        </Grid>
      </Container>
    </Box>

    {/* FAQ SECTION - PUBLIC PAGE for SEO */}
    <FAQSection
      faqs={[
        ...FAQTemplates.subscriptionGeneral,
        ...FAQTemplates.marketCoverage,
      ]}
      title="Frequently Asked Questions"
      subtitle="Everything you need to know about MarketCompass reports and subscriptions"
    />

    {/* Sample Reports Modal */}
    <SampleReportsModal
      open={sampleReportsOpen}
      onClose={() => setSampleReportsOpen(false)}
    />
    </>
  );
};

export default HomePage;


